import { chunk, compact, isEmpty, map, trim } from "lodash/fp";

import { formatNumber } from "@app/utils/utils";

/**
 * Helper method for creating a comma-separated list w/ Oxford comma and conjunction
 * */
export const formattedListWithConjunction = <T extends string>(
  items?: T[],
  conjunction = "and"
) => {
  if (items == null || isEmpty(items)) return "";

  if (items.length === 1) return items.toString();
  if (items.length === 2) return items.join(` ${conjunction} `);

  const [allButLastItem, lastItem] = chunk(items.length - 1, items);
  return `${allButLastItem.join(", ")}, ${conjunction} ${lastItem}`;
};

interface Pluralize {
  (word: string, pluralWord: string, count: number): string;
  (word: string, count: number, unused?: undefined): string;
}

export const pluralize: Pluralize = (word, pluralWordOrVal, val) => {
  let plural: string;
  let count;
  if (typeof pluralWordOrVal === "string") {
    plural = pluralWordOrVal;
    count = val;
  } else {
    plural = word === "person" ? "people" : `${word}s`;
    count = pluralWordOrVal;
  }
  // intentional double equals to also match against string values (e.g. "1.0")
  return count == 1 ? word : plural;
};

export const pluralizePhrase = (
  count: number,
  singularWord: string,
  opts?: {
    plural?: string;
    formatter?: (count: number) => string;
  }
) => {
  const formattedCount = (opts?.formatter ?? formatNumber)(count);

  if (opts?.plural) {
    return `${formattedCount} ${pluralize(singularWord, opts.plural, count)}`;
  }

  return `${formattedCount} ${pluralize(singularWord, count)}`;
};

export const withIndefiniteArticle = (word: string) =>
  word.match(/^[aeiou]/i) ? `an ${word}` : `a ${word}`;

export const toFormattedStringList = (
  items: string[],
  { bulleted, lineBreak }: { bulleted: boolean; lineBreak?: boolean } = {
    bulleted: true,
    lineBreak: false,
  }
) => {
  if (isEmpty(items)) return "";

  if (bulleted) {
    const [firstItem, ...restOfItems] = items;
    // prepend first list item with bullet
    return [`* ${firstItem}`, ...restOfItems].join("\n* ");
  }

  return items.join(`\n${lineBreak ? "\n" : ""}`);
};

export const splitParagraphs = (summary: string) =>
  compact(map(trim, (summary ?? "").split(/\n/)));

const PUNCTUATION_REGEX = /.*[,.?!;]+$/;
const PUNCTUATION_REPLACE_REGEX = /[,.?!;]+$/;

export const truncateAlongWordBoundary = (
  str: string | null | undefined,
  limit: number,
  { ellipsis }: { ellipsis?: boolean } = { ellipsis: true }
) => {
  if (!str || str.length <= limit) return str ?? "";

  let substring = str.slice(0, limit);
  const endsOnPunctuation = Boolean(substring.match(PUNCTUATION_REGEX));

  if (substring.includes(" ") && !endsOnPunctuation) {
    substring = substring.slice(0, substring.lastIndexOf(" "));
  }

  return `${substring.replace(PUNCTUATION_REPLACE_REGEX, "")}${
    ellipsis ? "..." : ""
  }`;
};
